export type CopilotRole = "system" | "user" | "assistant" | "tool";

export type CopilotToolCall = {
  /** id local del tool call */
  id: string;
  /** nombre de la skill */
  name: string;
  /** argumentos parseados */
  arguments: Record<string, unknown>;
  /** estado */
  status: "running" | "success" | "error";
  /** resultado serializado para mostrar al user */
  result?: unknown;
  error?: string;
  startedAt: number;
  endedAt?: number;
};

export type CopilotMessage = {
  id: string;
  role: CopilotRole;
  content: string;
  createdAt: number;
  pending?: boolean;
  error?: string;
  /** tool calls emitidos por este mensaje (solo en role=assistant) */
  toolCalls?: CopilotToolCall[];
  /** id del tool al que responde este mensaje (solo role=tool) */
  toolCallId?: string;
  /** Sugerencia inline para activar follow mode tras un guideUser */
  followSuggestion?: {
    title: string;
    skillName: string;
    args: Record<string, unknown>;
  };
  /** Botón "Continuar" tras llegar al límite de iteraciones */
  continuePrompt?: {
    /** Cuántos auto-continues ya se usaron en este turn (info al user) */
    autoContinuesUsed: number;
  };
};

export type CopilotMode = "follow" | "no-follow";

/**
 * Nivel de permiso que el user da al copiloto:
 *  - "ask"          → modal de confirmación SIEMPRE en cada acción de escritura
 *  - "accept-edits" → modal solo en acciones peligrosas (danger=true);
 *                     ediciones normales se ejecutan sin preguntar
 *  - "bypass"       → nunca pregunta, ejecuta todo directamente
 */
export type CopilotPermission = "ask" | "accept-edits" | "bypass";

/**
 * Modo de presentación del panel.
 *  - floating: overlay sobre el contenido (no lo desplaza)
 *  - immersed: push del layout (reduce el ancho del contenido). Solo desktop.
 */
export type CopilotPanelMode = "floating" | "immersed";

export type CopilotProviderId =
  | "orkestria"
  | "ollama-local"
  | "ollama-cloud"
  | "groq"
  | "openrouter"
  | "cerebras";

export type CopilotSettings = {
  providerId: CopilotProviderId;
  baseUrl: string;
  apiKey: string;
  model: string;
  mode: CopilotMode;
  panelMode: CopilotPanelMode;
  panelWidth: number;
  permission: CopilotPermission;
};

export const PANEL_MIN_WIDTH = 320;
export const PANEL_MAX_WIDTH = 720;
export const PANEL_DEFAULT_WIDTH = 400;

export type CopilotUsageWindow = {
  used: number;
  limit: number;
  remaining: number;
  exceeded: boolean;
  retryAfterSeconds?: number;
};

export type RestaurantUsageWindow = CopilotUsageWindow & {
  windowHours?: number;
};

/** Uso del copiloto dentro de un restaurante específico. */
export type CopilotRestaurantUsage = {
  /** Uso total del restaurante (todos los usuarios combinados) */
  total: {
    session: RestaurantUsageWindow & { windowHours: number };
    week: RestaurantUsageWindow;
  };
  /** Uso del usuario actual en este restaurante (según su rol) */
  user: {
    session: RestaurantUsageWindow & { windowHours: number };
    week: RestaurantUsageWindow;
    role: string | null;
  };
};

export type CopilotUsage = {
  session: CopilotUsageWindow & { windowHours: number };
  week: CopilotUsageWindow;
  /** true si el usuario tiene un plan activo (límites elevados: 50/sesión, 500/semana). */
  hasPlan?: boolean;
  /** Presente cuando hay un restaurante activo. */
  restaurant?: CopilotRestaurantUsage;
};

/** Configuración de cuota de un rol dentro del restaurante. */
export type CopilotRoleQuotaConfig = {
  role: string;
  sessionLimit: number;
  weekLimit: number;
};

/** Configuración completa de la cuota de IA del restaurante. */
export type CopilotRestaurantQuotaConfig = {
  sessionLimit: number;
  weekLimit: number;
  roleQuotas: CopilotRoleQuotaConfig[];
};

export type CopilotTaskStatus =
  | "pending"
  | "in-progress"
  | "done"
  | "failed"
  | "skipped";

export type CopilotTask = {
  id: string;
  label: string;
  status: CopilotTaskStatus;
  note?: string;
};

export type CopilotTaskList = {
  /** Resumen breve de la tarea global */
  title?: string;
  tasks: CopilotTask[];
  startedAt: number;
};

export type ProviderToolDef = {
  type: "function";
  function: {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  };
};

export type ProviderChatMessage = {
  role: CopilotRole;
  content: string;
  /** sólo en role=assistant si emitió tool calls */
  tool_calls?: Array<{
    id?: string;
    function: { name: string; arguments: Record<string, unknown> | string };
  }>;
  /** sólo en role=tool */
  tool_call_id?: string;
  name?: string;
};

export type ProviderChatArgs = {
  messages: ProviderChatMessage[];
  signal?: AbortSignal;
  systemPrompt: string;
  tools?: ProviderToolDef[];
  /** ID del turn del usuario para deduplicar usage events. Solo orkestria provider lo usa. */
  turnId?: string;
  /** ID del restaurante activo. Solo orkestria provider lo usa para cuotas de restaurante. */
  restaurantId?: string;
};

export type ProviderChatResult = {
  /** Texto del asistente (puede ser vacío si solo emite tool calls). */
  content: string;
  /** Tool calls emitidos por el modelo. */
  toolCalls: Array<{
    id: string;
    name: string;
    arguments: Record<string, unknown>;
  }>;
  /** Metadata opcional (usage, etc). Solo el provider 'orkestria' la rellena. */
  meta?: {
    usage?: CopilotUsage;
  };
};

export type CopilotProvider = {
  id: CopilotProviderId;
  label: string;
  defaultBaseUrl: string;
  defaultModel: string;
  /** List available models (best-effort; falls back to []). */
  listModels(args: { baseUrl: string; apiKey?: string }): Promise<string[]>;
  /** Send a chat completion request and return assistant message + tool calls. */
  chat(
    args: ProviderChatArgs & { baseUrl: string; apiKey?: string; model: string },
  ): Promise<ProviderChatResult>;
};
